import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Check, ChevronsUpDown, MapPin, Settings, TriangleAlert } from 'lucide-react';
import { cn } from '@repo/ui/src/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@repo/ui/src/components/ui/sidebar';
import { UNASSIGNED_BRANCH } from '../../stores/branchStore';
import { useBranchScope } from '../../hooks/useBranchScope';
import { useCan } from '../../hooks/useCan';

/**
 * The branch lens, as a scope row inside the sidebar between the logo and the
 * nav (design 1a).
 *
 * WHY here and not the header: the sidebar is the app's frame, so a scope placed
 * in it visibly WRAPS every nav item below — read top to bottom it says "this app
 * → this cơ sở → these pages". A control in the top-right corner has nothing to
 * wrap, which is why it read as "this page's filter" and why the header version
 * was replaced.
 *
 * DropdownMenu + SidebarMenuButton, deliberately NOT a Select: the menu needs
 * group hairlines, count badges and a navigation row, and a Select does none of
 * the three.
 *
 * Sizing: this is INSIDE the sidebar (1 unit = 4px), so the trigger reuses the
 * nav item's own classes (`h-8 rounded-lg text-[13.5px]`) rather than writing px
 * — that is what keeps it aligned if the token ever moves. The menu content is
 * portalled to <body>, OUTSIDE that scope, hence `w-[15rem]` and not `w-60`
 * (which would render 192px). Same trap as OrgMenu's `w-[18rem]`.
 */
export function BranchScopeRow() {
  const { t } = useTranslation('common');
  const scope = useBranchScope();
  // MEMBERs get the switcher but not the settings page it links to (that
  // route redirects them to their profile) — the row is a dead end for them
  // otherwise (final review F3).
  const canManageSettings = useCan('manage:settings');
  if (!scope.visible) return null;

  const scoped = scope.state !== 'all';

  return (
    <div className="px-2">
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                aria-label={t('branch.trigger')}
                tooltip={scope.label}
                className={cn(
                  'h-8 rounded-lg text-[13.5px]',
                  // Exactly the nav item's active classes, so the two read as one
                  // language: the row and the page it scopes look related.
                  scope.state === 'branch' && 'bg-brand-500/10 font-semibold text-brand-700',
                  // Attention, not brand — "Chưa gán" is not a place.
                  scope.state === 'unassigned' && 'bg-attention-tint font-semibold text-attention-foreground',
                )}
              >
                {/* Wrapped, so the button's own `[&>svg]:size-4` cannot shrink an
                    18px icon back to 16. Same span pattern the nav items use. */}
                <span className="flex size-[18px] shrink-0 items-center justify-center">
                  {scope.state === 'unassigned' ? (
                    <TriangleAlert className="size-[18px]" strokeWidth={1.9} />
                  ) : (
                    <MapPin
                      className={cn('size-[18px]', scope.state === 'branch' ? 'text-brand-600' : 'text-surface-500')}
                      strokeWidth={scope.state === 'branch' ? 1.9 : 1.8}
                    />
                  )}
                </span>
                <span
                  className={cn(
                    'flex-1 truncate group-data-[collapsible=icon]:hidden',
                    scope.state === 'all' && 'font-medium text-surface-500',
                  )}
                >
                  {scope.label}
                </span>
                <ChevronsUpDown
                  className={cn(
                    'ml-auto size-3.5 shrink-0 group-data-[collapsible=icon]:hidden',
                    scope.state === 'branch' ? 'text-brand-400' : 'text-surface-400',
                  )}
                  strokeWidth={1.8}
                />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-[15rem]">
              <DropdownMenuItem onSelect={() => scope.select(null)}>
                <ScopeCheck active={scope.state === 'all'} />
                <span className="flex-1">{t('branch.all')}</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator />

              {scope.branches.map((b) => (
                <DropdownMenuItem key={b.id} onSelect={() => scope.select(b.id)}>
                  <ScopeCheck active={scope.branchId === b.id} />
                  <span className="min-w-0 flex-1 truncate">{b.name}</span>
                  <span className="shrink-0 font-mono text-[11px] text-surface-400">{b.slotCount}</span>
                </DropdownMenuItem>
              ))}

              {/* Only while N > 0, so it disappears the moment the data is clean —
                  that is what keeps it a task rather than permanent noise. */}
              {scope.unassignedSlotCount > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => scope.select(UNASSIGNED_BRANCH)}>
                    <ScopeCheck active={scope.state === 'unassigned'} attention />
                    <span className="min-w-0 flex-1 truncate text-attention-foreground">{t('branch.unassigned')}</span>
                    <span className="flex h-[18px] shrink-0 items-center rounded-full bg-attention px-1.5 font-mono text-[10.5px] font-semibold text-attention-foreground">
                      {scope.unassignedSlotCount}
                    </span>
                  </DropdownMenuItem>
                </>
              )}

              {canManageSettings && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings/branches" className="text-[12.5px] text-surface-500">
                      <Settings className="size-3.5 shrink-0" strokeWidth={1.8} />
                      <span className="flex-1">{t('branch.manage')}</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      {/* The detail that makes the row "wrap" the nav: under a scope the hairline
          turns brand, so from any page you can see you are inside a lens. It
          keeps its height when transparent so the nav never shifts. */}
      <div className={cn('mt-2 h-px', scoped ? 'bg-brand-200' : 'bg-transparent')} />
    </div>
  );
}

/** The 14px check slot every menu row reserves, so the labels stay aligned
 *  whether or not a row is the active one. */
function ScopeCheck({ active, attention }: { active: boolean; attention?: boolean }) {
  return (
    <span className="flex size-3.5 shrink-0 items-center justify-center">
      {active && (
        <Check
          className={cn('size-3.5', attention ? 'text-attention-foreground' : 'text-brand-500')}
          strokeWidth={3}
        />
      )}
    </span>
  );
}
